import { colors } from '@lmc/common/constants'
import type {
  Content,
  ContentText,
  Margins,
  Node,
  StyleDictionary,
  TDocumentDefinitions
} from 'pdfmake/interfaces'
import { DOWNLOAD_TIMEOUT_MS, downloadPdf } from '../downloadPdf'
import { Header } from '../pdfContent'
import { tableStyles } from '../styles'
import type { PdfBlock, PdfDocument } from './pdfDocument'

const LABEL_WIDTH = 160

const MAX_FILE_NAME_LENGTH = 150

/** Headings are levels 1 and 2; a label over its content is this level. */
const LABEL_LEVEL = 3

/** Printed content: a leaf with text that isn't a heading or label. */
const isContent = (node: Node) => !node.headlineLevel && node.text !== undefined

/**
 * Moves a heading or label onto the next page when nothing it introduces
 * follows it on this one. Wrapping stacks and columns aren't content, so only
 * printed text counts; with nothing on the next page either, it stays.
 */
export const keepWithNext = (
  node: Node,
  followingNodesOnPage: Array<Node>,
  nodesOnNextPage: Array<Node>
) =>
  Boolean(node.headlineLevel) &&
  !followingNodesOnPage.some(isContent) &&
  nodesOnNextPage.some(isContent)

const styles: StyleDictionary = {
  // The shared styles are untyped: their margins read as `number[]`.
  ...(tableStyles as unknown as StyleDictionary),
  entryHeading: { bold: true, fontSize: 11, margin: [0, 6, 0, 4] },
  label: { bold: true, color: colors.darkgrey },
  sectionHeading: { bold: true, fontSize: 14, margin: [0, 18, 0, 8] }
}

/** pdfmake breaks lines on `\n` only, so other line endings are normalised. */
const withLineBreaks = (text: string) => text.replace(/\r\n?/g, '\n')

const heading = (text: string, level: number): ContentText => ({
  headlineLevel: level,
  style: level === 1 ? 'sectionHeading' : 'entryHeading',
  text: withLineBreaks(text)
})

const row = (labelText: string, value: string): Content => ({
  columnGap: 12,
  columns: [
    { style: 'label', text: withLineBreaks(labelText), width: LABEL_WIDTH },
    { text: withLineBreaks(value), width: '*' }
  ],
  margin: [0, 0, 0, 6]
})

/**
 * A block split for page breaks: `head` is its first unit (the whole of a
 * short row, a label with the first line or item of what it labels, a
 * heading with the head of its first block), which must not be split from
 * what comes before it; `rest` may break anywhere.
 */
type Parts = { head: Array<Content>; rest: Array<Content> }

/**
 * Longer than this, a first unit could be taller than a page, so it is left
 * breakable rather than overflow; its heading's `keepWithNext` still applies.
 */
const MAX_UNIT_LENGTH = 400
const MAX_UNIT_LINES = 8

const isShort = (text: string) =>
  text.length <= MAX_UNIT_LENGTH && text.split('\n').length <= MAX_UNIT_LINES

const GAP: Margins = [0, 0, 0, 6]
const INDENT: Margins = [12, 0, 0, 0]

/** Head and rest as content: the head as one unbreakable unit. */
const joined = ({ head, rest }: Parts): Array<Content> => [
  ...(head.length ? [{ stack: head, unbreakable: true }] : []),
  ...rest
]

const label = (text: string): ContentText => ({
  headlineLevel: LABEL_LEVEL,
  style: 'label',
  text: withLineBreaks(text)
})

/** A label over pieces, the first kept with the label when it is short. */
const labelledParts = (
  text: string | undefined,
  [first, ...more]: Array<Content>,
  firstIsShort: boolean
): Parts => {
  const labelNodes = text ? [label(text)] : []
  const last = (content: Content, isLast: boolean): Content =>
    isLast ? { margin: GAP, stack: [content] } : content
  const restNodes = more.map((content, index) =>
    last(content, index === more.length - 1)
  )
  const firstNode = last(first, more.length === 0)

  return firstIsShort
    ? { head: [...labelNodes, firstNode], rest: restNodes }
    : { head: labelNodes, rest: [firstNode, ...restNodes] }
}

const headedParts = (
  headingNode: ContentText,
  blocks: ReadonlyArray<PdfBlock>,
  margin?: Margins
): Parts => {
  const [first, ...others] = blocks
  const firstParts = first ? blockParts(first) : { head: [], rest: [] }
  const indented = (content: Array<Content>): Array<Content> =>
    margin && content.length ? [{ margin, stack: content }] : content

  return {
    head: [headingNode, ...indented(firstParts.head)],
    rest: indented([...firstParts.rest, ...others.flatMap(blockContent)])
  }
}

function blockParts(block: PdfBlock): Parts {
  switch (block.kind) {
    case 'entry':
      return headedParts(heading(block.heading, 2), block.blocks, INDENT)
    case 'line':
      // Complete in itself, so it may end a page.
      return {
        head: [{ style: 'entryHeading', text: withLineBreaks(block.text) }],
        rest: []
      }
    case 'list': {
      const [first = '', ...more] = block.items.map(withLineBreaks)
      return labelledParts(
        block.label,
        more.length ? [{ ul: [first] }, { ul: more }] : [{ ul: [first] }],
        isShort(first)
      )
    }
    case 'row': {
      const value = withLineBreaks(block.value)
      const content = row(block.label, value)
      return isShort(value)
        ? { head: [content], rest: [] }
        : { head: [], rest: [content] }
    }
    case 'text': {
      const [first, ...more] = withLineBreaks(block.value).split('\n')
      return labelledParts(
        block.label,
        more.length
          ? [{ text: first }, { text: more.join('\n') }]
          : [{ text: first }],
        isShort(first)
      )
    }
  }
}

function blockContent(block: PdfBlock): Array<Content> {
  return joined(blockParts(block))
}

/**
 * Lays any document out for pdfmake: the title, note and metadata, then
 * each section. Every heading and label is held with the first unit of what
 * it introduces (`Parts`), and `keepWithNext` backs that up where the unit is
 * too long to hold; pdfmake paginates the rest, breaking long text.
 */
export const toPdfDocDefinition = (
  document: PdfDocument
): TDocumentDefinitions => ({
  content: [
    // The header every other PDF has; its helper is untyped.
    Header({
      date: undefined,
      image: undefined,
      subtitle: document.subtitle,
      title: document.title
    }) as Content,
    ...(document.note
      ? [
          {
            margin: [0, 0, 0, 8],
            style: 'small',
            text: document.note
          } as Content
        ]
      : []),
    ...document.metadata.map(({ label, value }) => row(label, value)),
    ...document.sections.flatMap((section) =>
      joined(headedParts(heading(section.heading, 1), section.blocks))
    )
  ],
  defaultStyle: { fontSize: 10 },
  footer: (currentPage, pageCount) => ({
    margin: [0, 5],
    style: ['small', 'center'],
    text: `${currentPage} / ${pageCount}`
  }),
  info: { title: document.title },
  pageBreakBefore: keepWithNext,
  styles
})

/**
 * A safe file name ending in `.pdf`: characters a file system rejects become
 * `-`, and a dot in the name can't be taken for the extension.
 */
export const toPdfFileName = (name: string) => {
  const safe = name
    // biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what this strips
    .replace(/[\\/:*?"<>|\u0000-\u001F]/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/\.pdf$/i, '')
    .slice(0, MAX_FILE_NAME_LENGTH)
    .replace(/[\s.]+$/, '')
    .trim()

  return `${safe || 'document'}.pdf`
}

/**
 * Downloads a document as a PDF with the app's shared pdfmake download,
 * resolving once the file is handed to the browser. Rejects if pdfmake fails
 * or takes longer than `timeoutMs`.
 */
export const downloadPdfDocument = (
  document: PdfDocument,
  fileName: string,
  timeoutMs = DOWNLOAD_TIMEOUT_MS
) =>
  downloadPdf(toPdfDocDefinition(document), toPdfFileName(fileName), timeoutMs)
